import moment, { Moment } from "moment";

/**
 * Format date to Buddhist Era (BE) date string
 * @param date - Date object, Moment object, or date value to format
 * @returns Date string in DD/MM/YYYY (BE) format or empty string if invalid
 */
export function formatDateToStringBe(date: any) {
  return formatDate(date);
}

/**
 * Convert date to DD/MM/YYYY format in Buddhist Era (BE)
 * @param date - Date object, Moment object, or date value to format
 * @returns Formatted date string DD/MM/YYYY (BE) or empty string if invalid
 */
export function formatDate(date: any) {
  if (moment.isMoment(date)) {
    date = date.toDate();
  }
  if (date instanceof Date && !isNaN(date.valueOf())) {
    const day = `${date.getDate()}`.padStart(2, "0");
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const year = `${date.getFullYear() + 543}`;
    return `${day}/${month}/${year}`;
  }
  return "";
}

type PDReturnType<T extends boolean | undefined> = T extends boolean
  ? T extends false
    ? Date
    : Moment
  : Date;

/**
 * Parse date string in DD/MM/YYYY (BE) format to Date or Moment object
 * @param str - Date string in DD/MM/YYYY (BE) format
 * @param toMoment - If true, returns Moment object; if false or undefined, returns Date object
 * @returns Date or Moment object based on toMoment parameter, or undefined if invalid
 */
export function parseDate<S extends string, T extends boolean | undefined = undefined>(
  str: S,
  toMoment?: T
): PDReturnType<T> | undefined {
  if (typeof str !== "string") {
    return;
  }

  const split = str.split("/");

  if (split.length !== 3) {
    return;
  }

  const year = parseInt(split[2], 10) - 543;
  const month = parseInt(split[1], 10) - 1;
  const day = parseInt(split[0], 10);

  if (
    isNaN(year) ||
    String(year).length > 4 ||
    isNaN(month) ||
    isNaN(day) ||
    day <= 0 ||
    day > 31 ||
    month < 0 ||
    month >= 12
  ) {
    return;
  }

  // When using this function with DayPickerInput, the second argument will be a format not toMoment
  if (toMoment === true) {
    return moment(`${year}-${month + 1}-${day}`, "YYYY-MM-DD") as PDReturnType<T>;
  }

  // Handle the case where toMoment is false or undefined
  return new Date(year, month, day) as PDReturnType<T>;
}

// Data yyyy-mm-dd (AD) to dd/mm/yyyy (BE)
// export function formatADtoBEString(dateString) {
//   if (typeof dateString !== 'string') {
//     return;
//   }

//   const split = dateString.split('-');
//   if (split.length !== 3) {
//     return;
//   }

//   const year = parseInt(split[0], 10) + 543;
//   // const month = parseInt(split[1], 10) ;
//   // const day = parseInt(split[2], 10);
//   return split[2] + "/" + split[1] + "/" + year.toString()
// }

/**
 * Convert date string from YYYY-MM-DD (AD) to DD/MM/YYYY (BE) format
 * @param dateString - Date string in YYYY-MM-DD (AD) format
 * @param spliter - Separator character in input date string (default: "-")
 * @returns Date string in DD/MM/YYYY (BE) format, or undefined if invalid
 */
export function formatADtoBEString(dateString: any, spliter = "-") {
  if (typeof dateString !== "string") {
    return;
  }

  const split = dateString.split(spliter);
  if (split.length !== 3) {
    return;
  }

  const year = parseInt(split[0], 10) + 543;
  // const month = parseInt(split[1], 10) ;
  // const day = parseInt(split[2], 10);
  return split[2] + "/" + split[1] + "/" + year.toString();
}

/**
 * format date string dd/mm/yyyy (BE) to yyyy<separator>mm<separator>dd (AD)
 * @param {string} dateString - date in dd/mm/yyyy
 * @param {string} separator - separator (default: '-')
 * @param {boolean} fromBE - convert from BE to AD when `true`,
 *                           `false` if do not convert (default: true)
 * @return {any} - return string of formatted date if dateString is a valid date string
 *                 else return dateString without do anything
 */
export function formatDateToYYYYMMDD(dateString: any, separator?: any, fromBE?: any) {
  separator = separator || "-";
  fromBE = fromBE != null ? fromBE : true;

  if (typeof dateString !== "string") {
    return dateString;
  }

  const dateSplitted = dateString.split("/");
  if (dateSplitted.length !== 3) {
    return dateString;
  }

  let year;
  if (fromBE) {
    year = String(parseInt(dateSplitted[2], 10) - 543);
  } else {
    year = String(parseInt(dateSplitted[2], 10));
  }

  const month = String(parseInt(dateSplitted[1], 10)).padStart(2, "0");
  const day = String(parseInt(dateSplitted[0], 10)).padStart(2, "0");

  return year + separator + month + separator + day;
}

/**
 * Convert UTC datetime string to BE date and time format
 * @param utcDT - UTC datetime string (e.g., "2020-06-22T17:17:32Z")
 * @param timeFormat - Time format string (default: "HH:mm")
 * @returns Array containing [date in DD/MM/YYYY (BE), time in specified format]
 */
export function formatUTCtoBEString(utcDT: any, timeFormat = "HH:mm") {
  const dateObj = new Date(utcDT);
  const mm = moment(dateObj);
  const dateBe = formatADtoBEString(mm.format("YYYY-MM-DD"));
  const timeBe = mm.format(timeFormat);

  return [dateBe, timeBe];
}

/**
 * Convert UTC datetime string to Moment object
 * @param utcDT - UTC datetime string (e.g., "2020-06-22T17:17:32Z")
 * @returns Moment object created from the UTC datetime
 */
export function formatUTCtoMoment(utcDT: any) {
  const dateObj = new Date(utcDT);
  return moment(dateObj);
}

/**
 * Convert ISO datetime string to Thai date format with Bangkok timezone (UTC+7)
 * @param isoString - ISO datetime string
 * @returns Date string in DD/MM/YYYY [HH:mm] (BE) format
 */
export function formatISOToThaiDate(isoString: string) {
  const date = new Date(isoString);

  // Convert to UTC+7 (Bangkok Time)
  const options = {
      timeZone: 'Asia/Bangkok', // UTC+7
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // 24-hour format
  };

  const formatter = new Intl.DateTimeFormat('en-GB', options);
  const parts = formatter.formatToParts(date);

  // Extract the formatted parts
  const day = parts?.find(p => p.type === 'day')?.value;
  const month = parts?.find(p => p.type === 'month')?.value;
  const year = parseInt(parts?.find(p => p.type === 'year')?.value) + 543; // Convert to BE
  const hour = parts?.find(p => p.type === 'hour')?.value;
  const minute = parts?.find(p => p.type === 'minute')?.value;

  return `${day}/${month}/${year} [${hour}:${minute}]`;
}

/**
 * Convert ISO date string to DD/MMM/YYYY format
 * @param isoDate - ISO date string
 * @returns Date string in DD/MMM/YYYY format (e.g., "15/Jan/2023")
 */
export function formatISOtoDDMMMYYYY(isoDate: string) {
  // console.log('isoDate: ', isoDate);
  const dateObj = new Date(isoDate);
  // console.log('dateObj: ', dateObj);
  let month = dateObj.toLocaleString("default", { month: "short" });
  // console.log('month: ', month);
  let ddmmmyyyy = dateObj.getDate() + "/" + month + "/" + dateObj.getFullYear();
  // console.log('ddmmmyyyy: ', ddmmmyyyy);
  return ddmmmyyyy;
}

/**
 * Validate if string matches DD/MM/YYYY date format
 * @param str - String to validate
 * @returns True if string matches DD/MM/YYYY format, false otherwise
 */
export const isValidDate = (str: any) =>
  /^(0[1-9]|1\d|2\d|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/.test(str);

/**
 * Format datetime value to DD/MM/YYYY HH:mm (BE) format
 * @param value - Date value to format (Date, Moment, or date string)
 * @param squareBrackets - If true, wraps time in square brackets [HH:mm] (default: false)
 * @returns Formatted datetime string in DD/MM/YYYY HH:mm (BE) or DD/MM/YYYY [HH:mm] (BE), or empty string if invalid
 */
export function formatDatetime(value: any, squareBrackets = false) {
  if (value) {
    let datetime = moment(value);
    let day = datetime.format("DD");
    let month = datetime.format("MM");
    let year = parseInt(datetime.format("YYYY")) + 543;
    let time = datetime.format("HH:mm");

    time = squareBrackets ? `[${time}]` : time;

    return `${day}/${month}/${year} ${time}`;
  } else {
    return "";
  }
}

/**
 * Convert date object to DD/MM/YYYY (BE) format without time
 * @param value - Date value to format (Date, Moment, or date string)
 * @returns Date string in DD/MM/YYYY (BE) format (e.g., "23/08/2566"), or empty string if invalid
 */
export function dateToStringWithoutTimeBE(value: any) {
  // console.log("dateToStringWithoutTimeBE value" ,value)
  if (value) {
    var datetime = moment(value);
    var day = datetime.format("DD");
    var month = datetime.format("MM");
    var year = parseInt(datetime.format("YYYY")) + 543;
    // console.log("dateToStringWithoutTimeBE", day, "/", month, "/", year)
    return day + "/" + month + "/" + year;
  } else {
    return "";
  }
}

/**
 * Convert date object to DD/MM/YYYY (AD) format without time
 * @param value - Date value to format (Date, Moment, or date string)
 * @returns Date string in DD/MM/YYYY (AD) format (e.g., "23/08/2023"), or empty string if invalid
 */
export function dateToStringWithoutTimeAD(value: any): string {
  if (value) {
    var datetime = moment(value);
    var day = datetime.format("DD");
    var month = datetime.format("MM");
    var year = parseInt(datetime.format("YYYY"));
    return day + "/" + month + "/" + year;
  } else {
    return "";
  }
}

/**
 * Convert DD/MM/YYYY (BE) date string to Moment object in AD
 * @param str - Date string in DD/MM/YYYY (BE) format
 * @returns Moment object with AD year, or undefined if invalid
 */
export function beToAd(str: string) {
  if (str && typeof str === "string") {
    const tokens = str.split("/");
    const date = `${parseInt(tokens[2]) - 543}-${tokens[1]}-${tokens[0]}`;
    return moment(date);
  } else {
    return;
  }
}

/**
 * Check if DD/MM/YYYY (BE) date string represents today's date
 * @param str - Date string in DD/MM/YYYY (BE) format
 * @returns True if the date is today, false otherwise
 */
export const isTodayBEString = (str: string) => {
  if (str?.length !== 10) {
    console.warn("not in format BE String");
    return false;
  }
  const tokens = str.split("/");
  let inputDate = new Date(parseInt(tokens[2]) - 543, parseInt(tokens[1]) - 1, parseInt(tokens[0]));

  let today = new Date();

  if (inputDate.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0)) {
    return true;
  }

  return false;
};

/**
 * Convert DD/MM/YYYY (BE) date string to DD/MM/YYYY (AD) format
 * @param beString - Date string in DD/MM/YYYY (BE) format
 * @returns Date string in DD/MM/YYYY (AD) format, or undefined if invalid
 */
export function beStringToAdString(beString: string): string | undefined {
  if (beString && typeof beString === "string") {
    const tokens = beString.split("/");
    const dateAD = `${tokens[0]}/${tokens[1]}/${parseInt(tokens[2]) - 543}`;
    return dateAD;
  }
  return undefined;
}

/**
 * Convert DD/MM/YYYY (BE) date string to JavaScript Date object
 * @param beString - Date string in DD/MM/YYYY (BE) format
 * @returns JavaScript Date object with AD year, or current date if invalid
 */
export function beStringToDateObject(beString: string): Date {
  if (beString && typeof beString === "string") {
    const tokens = beString.split("/");
    return new Date(parseInt(tokens[2]) - 543, parseInt(tokens[1]) - 1, parseInt(tokens[0]));
  }
  return new Date();
}

/**
 * Convert YYYY-MM-DD (AD), HH:mm date string to JavaScript Date object
 */
export function adStringToDateObject(date: string, time: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);

  return new Date(year, month - 1, day, hour, minute);
}

/**
 * Convert DD/MM/YYYY (BE) date string to Moment object
 * @param beString - Date string in DD/MM/YYYY (BE) format
 * @returns Moment object with AD year, or current date moment if invalid
 */
export function beStringToMoment(beString: string): Moment {
  let date = new Date()
  if (beString && typeof beString === "string") {
    const tokens = beString.split("/");
    date = new Date(parseInt(tokens[2]) - 543, parseInt(tokens[1]) - 1, parseInt(tokens[0]));
  }
  return moment(date)
}

/**
 * Convert DD/MM/YYYY-HH:mm (BE) datetime string to JavaScript Date object
 * @param beDateTimeString - Datetime string in DD/MM/YYYY-HH:mm (BE) format
 * @returns JavaScript Date object with AD year, or current date if invalid
 */
export function beStringDateTimeToDateObject(beDateTimeString: string): Date {
  if (beDateTimeString && typeof beDateTimeString === "string") {
    const dateTime = beDateTimeString.split("-");
    const date = dateTime[0].split("/");
    const time = dateTime[1].split(":");
    if (date && time) {
      return new Date(
        parseInt(date[2]) - 543,
        parseInt(date[1]) - 1,
        parseInt(date[0]),
        parseInt(time[0]),
        parseInt(time[1]),
        0
      );
    }
  }
  return new Date();
}

/**
 * Convert DD/MM/YYYY (BE) date string to JavaScript Date object (time set to 00:00)
 * @param beDateTimeString - Date string in DD/MM/YYYY (BE) format
 * @returns JavaScript Date object with AD year and time set to midnight, or current date if invalid
 */
export function beStringDateToDateObject(beDateTimeString: string): Date {
  if (beDateTimeString && typeof beDateTimeString === "string") {
    const date = beDateTimeString.split("/");
    if (date && date?.length === 3) {
      return new Date(parseInt(date[2]) - 543, parseInt(date[1]) - 1, parseInt(date[0]), 0);
    }
  }
  return new Date();
}

/**
 * Convert YYYY-MM-DD/HH:mm (AD) datetime string to JavaScript Date object
 * @param adateTimeString - Datetime string in YYYY-MM-DD/HH:mm (AD) format
 * @returns JavaScript Date object, or current date if invalid
 */
export function YYYYMMDDadStringDateTimeToDateObject(adateTimeString: string): Date {
  if (adateTimeString && typeof adateTimeString === "string") {
    const dateTime = adateTimeString.split("/");
    console.log("dateTime: ", dateTime);
    const date = dateTime[0].split("-");
    console.log("date: ", date);
    const time = dateTime[1].split(":");
    console.log("time: ", time);
    if (date && time) {
      return new Date(
        parseInt(date[0]),
        parseInt(date[1]) - 1,
        parseInt(date[2]),
        parseInt(time[0]),
        parseInt(time[1]),
        0
      );
    }
  }
  return new Date();
}

/**
 * Convert Date object to DD/MM/YYYY HH:mm (AD) format
 * @param date - Date object or date value
 * @returns Date string in DD/MM/YYYY HH:mm (AD) format
 */
export function dateToStringAD(date: any) {
  const mm = moment(date);
  const dateAd = mm.format("DD/MM/YYYY HH:mm");
  return dateAd;
}

/**
 * Convert Date object to YYYY-MM-DD HH:mm (AD) format
 * @param date - Date object or date value
 * @returns Date string in YYYY-MM-DD HH:mm (AD) format
 */
export function dateToStringADDash(date: any) {
  const mm = moment(date);
  const dateAd = mm.format("YYYY-MM-DD HH:mm");
  return dateAd;
}

/**
 * Convert Date object to DD/MM/YYYY HH:mm (BE) format
 * @param date - Date object or date value
 * @returns Date string in DD/MM/YYYY HH:mm (BE) format
 */
export function dateToStringBE(date: any) {
  const mm = moment(date);
  const year = Number(mm.format("YYYY")) + 543;
  const dateBe1 = mm.format("DD/MM/");
  const dateBe2 = mm.format("HH:mm");
  return dateBe1 + year + " " + dateBe2;
}

/**
 * Convert Moment object to DD/MM/YYYY HH:mm (BE) format
 * @param mm - Moment object
 * @returns Date string in DD/MM/YYYY HH:mm (BE) format
 */
export function momentToStringBE(mm: Moment) {
  const year = Number(mm.format("YYYY")) + 543;
  const dateBe1 = mm.format("DD/MM/");
  const dateBe2 = mm.format("HH:mm");
  return dateBe1 + year + " " + dateBe2;
}

/**
 * Convert DD-MM-YYYY HH:mm (BE) datetime string to DD-MM-YYYY HH:mm (AD) format
 * @param timeStr - Datetime string in DD-MM-YYYY HH:mm (BE) format (e.g., "22-06-2567 09:00")
 * @returns Datetime string in DD-MM-YYYY HH:mm (AD) format, or empty string if invalid
 */
export function timeStrBEToStringAD(timeStr: string) {
  const [date, ...timeParts] = timeStr.split(" ");
  const [day, month, yearBE] = date.split("-");

  if (!(timeParts.length > 0 && day && month && yearBE)) {
    return "";
  }

  const yearAD = Number(yearBE) - 543;
  const dateAD = [day, month, yearAD].join("-");

  return yearAD > 0 ? `${dateAD} ${timeParts.join(" ")}` : "";
}

/**
 * Get today's date as YYYY-MM-DD (AD) string
 * @returns Today's date in YYYY-MM-DD (AD) format (e.g., "2020-06-22")
 */
export function today_string_ad() {
  const dateObj = new Date();
  const mm = moment(dateObj);
  const dateBe = mm.format("YYYY-MM-DD");
  return dateBe;
}

/**
 * Convert AD date string to BE date string (deprecated - use convertToBEDatetime instead)
 * @param str - Date string in AD format
 * @param format - Input date format (default: "DD/MM/YYYY")
 * @returns Date string in DD/MM/YYYY (BE) format (e.g., "20/01/2000" -> "20/01/2543"), or empty string if invalid
 */
export function adToBe(str: string, format = "DD/MM/YYYY"): string {
  //! Use convertToBEDatetime instead
  if (str) {
    const momentDate = moment(str, format);
    return `${momentDate.format("DD/MM/")}${parseInt(momentDate.format("YYYY")) + 543}`;
  } else {
    return "";
  }
}

/**
 * Extract time from date object in HH:mm format
 * @param value - Date object, Moment, or date value
 * @returns Time string in HH:mm format, or empty string if invalid
 */
export function formatTimeCurrent(value: any) {
  if (value) {
    var datetime = moment(value);
    var time = datetime.format("HH:mm");
    return time;
  } else {
    return "";
  }
}

/**
 * Convert AD date to BE format with custom format strings
 * @param dateAd - AD date string
 * @param adFormat - Input date format (default: "YYYY-MM-DD")
 * @param beFormat - Output BE date format (default: "DD/MM/YYYY")
 * @param locale - Locale for formatting (default: "en")
 * @returns Formatted BE date string, or empty string if invalid
 */
export function adToBeWithSetFormat(
  dateAd: string,
  adFormat: string = "YYYY-MM-DD",
  beFormat: string = "DD/MM/YYYY",
  locale: string = "en"
): string {
  if (dateAd) {
    const momentDate = moment(dateAd, adFormat);
    // find year be format
    let yearFormat = "YYYY";
    if (beFormat.includes("YYYY")) {
      yearFormat = "YYYY";
    } else if (beFormat.includes("YY")) {
      yearFormat = "YY";
    }

    // set year value
    let yearAD = momentDate.format("YYYY");
    let yearBE = (parseInt(yearAD) + 543).toString();
    if (yearFormat == "YY") {
      yearBE = yearBE.slice(2, 4);
    }
    let yearSetFormat = beFormat.replace(yearFormat, yearBE);
    return momentDate.locale(locale).format(yearSetFormat);
  } else {
    return "";
  }
}

/**
 * Convert YYYY-MM-DD HH:mm to DD/MM/YYYY HH:mm in AD or BE format (for report raksthai)
 * @param input - Datetime string in YYYY-MM-DD HH:mm format (default: "")
 * @param isAd - If true, returns AD format; if false, returns BE format (default: false)
 * @returns Datetime string in DD/MM/YYYY HH:mm (AD or BE), or empty string if invalid
 */
export const convertLastUpdate = (input: string = "", isAd: boolean = false): string => {
  if (!input) {
    return "";
  }

  let date = moment(input, "YYYY-MM-DD HH:mm");
  let convertDate = "";
  if (isAd) {
    //
    convertDate = dateToStringAD(date);
  } else {
    convertDate = dateToStringBE(date);
  }

  return convertDate;
};

/**
 * Convert ISO datetime string to DD/MM/YYYY (BE) format, or return as-is if already in DD/MM/YYYY format
 * @param strDate - ISO datetime string or DD/MM/YYYY date string
 * @returns Date string in DD/MM/YYYY (BE) format, or original string if not ISO
 */
export const convertISOtoBEIfISO = (strDate: string) => {
  console.log("convertISOtoBEIfISO strDate: ", strDate);
  if (strDate && strDate?.length > 10 && moment(strDate, moment.ISO_8601).isValid()) {
    return dateToStringWithoutTimeBE(new Date(strDate));
  } else {
    return strDate;
  }
};

/**
 * Extract time from ISO date string in HH:mm format
 * @param isoDate - ISO date string
 * @returns Time string in HH:mm format (e.g., "14:30")
 */
export function formatISOtoHHmm(isoDate: string) {
  // console.log('isoDate: ', isoDate);
  const dateObj = new Date(isoDate);
  // console.log('dateObj: ', dateObj);
  let hours = dateObj.getHours().toString().padStart(2, "0");
  // console.log('hours', hours);
  let minutes = dateObj.getMinutes().toString().padStart(2, "0");
  // console.log('minutes', minutes);
  let hhmm = hours + ":" + minutes;
  // console.log('hhmm: ', hhmm);
  return hhmm;
}

/**
 * Convert Moment (AD) to local format with BE year (e.g., 2566-02-04T10:35:24-08:00)
 * @param dateMoment - Moment object with AD year
 * @returns ISO format string with BE year and local timezone
 */
export function momentADToLocalFormatBE( dateMoment: Moment)  {
  let beString = dateMoment?.format()
  let [yearAD, ...rest] = beString.split("-")
  let yearBE = Number(yearAD) + 543
  let prepare = [yearBE, ...rest]
  return prepare.join("-")
}

/**
 * Convert Moment to YYYY-MM-DD (BE) format
 * @param dateMoment - Moment object with AD year
 * @param formatString - Output format string (default: "YYYY-MM-DD")
 * @returns Formatted date string with BE year (e.g., "2566-05-01")
 */
export function momentToYYYYMMDD( dateMoment: Moment, formatString: string = "YYYY-MM-DD" ) {
  let beString = dateMoment?.format(formatString)
  let [yearAD, ...rest] = beString.split("-")
  let yearBE = Number(yearAD) + 543
  let prepare = [yearBE, ...rest]
  return prepare.join("-")
}


/**
 * Convert ISO date to DD MonthName YYYY (BE) format in Thai
 * @param value - ISO date string or date value
 * @returns Date string in Thai format (e.g., "15 มกราคม 2566"), or empty string if invalid
 */
export function formatISOtoBEDate(value: any) {
  if (value) {
    var datetime = moment(value);
    var day = datetime.format("DD");
    var month = datetime.format("MM");
    var year = parseInt(datetime.format("YYYY")) + 543;
    let monthText = MONTH_OPTION.find((items: any) => items?.number === month)?.text

    return `${day} ${monthText} ${year}`
  } else {
    return "";
  }
}

/**
 * Convert ISO datetime to various BE datetime formats
 * @param value - ISO datetime string or date value
 * @returns Object containing multiple formatted strings:
 *   - date: DD/MM/YYYY (BE)
 *   - time: HH:mm:ss
 *   - dateTime: DD/MM/YYYY HH:mm:ss
 *   - dateTimeShort: DD/MM/YYYY HH:mm
 *   - dateTimeShortBracket: DD/MM/YYYY [HH:mm]
 *   Returns undefined for all fields if invalid
 */
export function formatISOtoBEDateTime(value: any) {
  const dateTime = moment(value);

  if (!dateTime.isValid()) {
    return {
      date: undefined,
      time: undefined,
      dateTime: undefined,
      dateTimeShort: undefined,
      dateTimeShortBracket: undefined,
    };
  }

  const beYear = dateTime.year() + 543;
  const date = dateTime.format(`DD/MM`).concat(`/${beYear}`);
  const time = dateTime.format(`HH:mm:ss`);
  const timeShort = dateTime.format(`HH:mm`);

  return {
    date,
    time,
    timeShort,
    dateTime: `${date} ${time}`,
    dateTimeShort: `${date} ${timeShort}`,
    dateTimeShortBracket: `${date} [${timeShort}]`,
  };
};

/**
 * Convert ISO date to full Thai date format with weekday
 * @param value - ISO date string or date value
 * @param detailDay - Optional suffix for the day name (e.g., "ที่")
 * @returns Full Thai date string (e.g., "วันจันทร์ 15 มกราคม 2566"), or empty string if invalid
 */
export function formatISOToFullBEDate(value: any, detailDay?: string) {
  if (value) {
    let datetime = moment(value);
    let day = datetime.format("D");
    let year = parseInt(datetime.format("YYYY")) + 543;

    let weekDayRaw = datetime.format("dddd");
    let weekDay = weekDayRaw as keyof typeof WEEK_DAY_THAI;
    let dayText = WEEK_DAY_THAI[weekDay] ?? "ไม่พบชื่อวัน";

    let month = datetime.format("MM");
    let monthText = MONTH_OPTION.find((items: any) => items?.number === month)?.text

    return `${dayText}${detailDay || ""} ${day} ${monthText} ${year}`
  } else {
    return "";
  }
}

/**
 * Convert AD date to Thai text date format with BE year
 * @param value - Date string in YYYY-MM-DD, DD-MM-YYYY, or DD/MM/YYYY (AD) format
 * @param isShortMonth - If true, uses short month names (e.g., "ม.ค."); if false, uses full names (default: false)
 * @returns Thai date string (e.g., "15 มกราคม 2566"), or empty string if invalid
 */
export function formatADDateToThaiTextDate(value: any, isShortMonth: boolean = false) {
  if (!value) return "";

  const m = moment(value, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY"], true);
  if (!m.isValid()) return "";

  const day = m.date();
  const month = m.month() + 1;
  const yearBE = m.year() + 543;

  const monthOptions = MONTH_OPTION.find((m) => m.value === month);
  const monthText = isShortMonth ? monthOptions?.short : monthOptions?.text || "";

  return `${day} ${monthText} ${yearBE}`;
};

export const formatLocalizedDateTime = (date: string, locale = "th") => {
  if (!date) {
    return "";
  }

  const m = moment(date).clone().locale(locale);
  const day = m.format("DD");
  const month = m.format("MMMM");
  const year = locale === "th" ? m.year() + 543 : m.year();
  const time = m.format("HH:mm");

  return `${day} ${month} ${year} ${time}`;
};

/**
 * Convert date format from YYYY-MM-DD to DD/MM/YYYY (keeps same year type)
 * @param value - Date string in YYYY-MM-DD format, or null
 * @returns Date string in DD/MM/YYYY format, or empty string if invalid/null
 */
export function formatDateDashToSlash(value: string | null): string {
  if (!value) return "";

  const [year, month, day] = value.split("-");

  if (!year || !month || !day) return "";

  return `${day}/${month}/${year}`;
};

/**
 * Get default filter date range for current month in BE format
 * @returns Object with start_date (first day of month) and end_date (last day of month) in DD/MM/YYYY (BE) format
 * @example { start_date: "01/01/2568", end_date: "31/01/2568" }
 */
export const getDefaultFilterDate = () => {
  const startDate = moment().startOf("month");
  const endDate = moment().endOf("month");

  return {
    start_date: dateToStringWithoutTimeBE(startDate),
    end_date: dateToStringWithoutTimeBE(endDate),
  }
};

export const MONTH_OPTION = [
  { value: 1, key: 1, text: "มกราคม", short: "ม.ค.", number: "01" },
  { value: 2, key: 2, text: "กุมภาพันธ์", short: "ก.พ.", number: "02" },
  { value: 3, key: 3, text: "มีนาคม", short: "มี.ค.", number: "03" },
  { value: 4, key: 4, text: "เมษายน", short: "เม.ย.", number: "04" },
  { value: 5, key: 5, text: "พฤษภาคม", short: "พ.ค.", number: "05" },
  { value: 6, key: 6, text: "มิถุนายน", short: "มิ.ย.", number: "06" },
  { value: 7, key: 7, text: "กรกฎาคม", short: "ก.ค.", number: "07" },
  { value: 8, key: 8, text: "สิงหาคม", short: "ส.ค.", number: "08" },
  { value: 9, key: 9, text: "กันยายน", short: "ก.ย.", number: "09" },
  { value: 10, key: 10, text: "ตุลาคม", short: "ต.ค.", number: "10" },
  { value: 11, key: 11, text: "พฤศจิกายน", short: "พ.ย.", number: "11" },
  { value: 12, key: 12, text: "ธันวาคม", short: "ธ.ค.", number: "12" },
];

export const MONTH_EN_OPTION = [
  { value: 1, key: 1, text: "January", short: "Jan.", number: "01" },
  { value: 2, key: 2, text: "February", short: "Feb.", number: "02" },
  { value: 3, key: 3, text: "March", short: "Mar.", number: "03" },
  { value: 4, key: 4, text: "April", short: "Apr.", number: "04" },
  { value: 5, key: 5, text: "May", short: "May", number: "05" },
  { value: 6, key: 6, text: "June", short: "Jun.", number: "06" },
  { value: 7, key: 7, text: "July", short: "Jul.", number: "07" },
  { value: 8, key: 8, text: "August", short: "Aus.", number: "08" },
  { value: 9, key: 9, text: "September", short: "Sep.", number: "09" },
  { value: 10, key: 10, text: "October", short: "Oct.", number: "10" },
  { value: 11, key: 11, text: "November", short: "Nov.", number: "11" },
  { value: 12, key: 12, text: "December", short: "Dec.", number: "12" },
];

export const WEEK_DAY_THAI = {
  Monday: "วันจันทร์",
  Tuesday: "วันอังคาร",
  Wednesday: "วันพุธ",
  Thursday: "วันพฤหัสบดี",
  Friday: "วันศุกร์",
  Saturday: "วันเสาร์",
  Sunday: "วันอาทิตย์"
}

/**
 * Format time string to both Thai and English formats
 * @param timeString - Time string in HH:mm format or full datetime string
 * @returns Object with time_en (12-hour with AM/PM) and time_th (12-hour with น.) formats
 * @example { time_en: "02:30 PM", time_th: "02:30 น." }
 */
export const formatTime = (timeString: string) => {
  // เช็คว่าเป็น time หรือไม่ (HH:MM format)
  const isTimeOnly = /^\d{1,2}:\d{2}$/.test(timeString);

  const momentTime = isTimeOnly ? moment(timeString, "HH:mm") : moment(timeString);

  // Thai time format with น.
  const formatTh = `${momentTime.format("hh:mm")} น.`;

  // English time format with AM/PM
  const formatEn = momentTime.format("hh:mm A");

  return {
    time_en: formatEn,
    time_th: formatTh,
  };
};

const YEAR_THRESHOLD = moment().year() + (543 / 2);

/**
 * Validate if date string is in AD (Christian Era) format
 * @param date - Date string to validate
 * @param isStrictYear - If true, enforces strict year validation and 4-digit year format (default: false)
 * @returns True if date is valid AD format with year <= threshold, false otherwise
 */
export const isValidADDate = (date: string, isStrictYear = false, adFormat?: string) => {
  if (typeof date !== "string") {
    return false;
  }

  const parsed = moment(date, adFormat);

  if (!parsed.isValid()) {
    return false;
  }

  const year = parsed.year();
  const isYearValidRange = year <= YEAR_THRESHOLD;

  return isStrictYear ? isYearValidRange && /\d{4}/.test(date) : isYearValidRange;
};

/**
 * Convert incomplete date string to complete date format (fills missing day/month with "01")
 * @param dateString - Incomplete or complete date string (supports YYYY, MM/YYYY, YYYY-MM, or full dates)
 * @param formatString - Output format: "DD/MM/YYYY" for BE or "YYYY-MM-DD" for AD (default: "DD/MM/YYYY")
 * @returns Complete date string in specified format, or null if invalid
 * @example formatMissingDate("2023", "DD/MM/YYYY") returns "01/01/2566" (converts to BE)
 */
export const formatMissingDate = (
  dateString: string,
  formatString: "DD/MM/YYYY" | "YYYY-MM-DD" = "DD/MM/YYYY"
): string | null => {
  if (!dateString) {
    return null;
  }

  const useAdFormat = formatString === "YYYY-MM-DD";
  const parts = dateString.split(/[/-]/).filter(Boolean);

  if (parts.length === 0) {
    return null;
  }

  // ค่า default สำหรับวันที่ไม่ครบ
  let day = "01";
  let month = "01";
  let year = "";

  if (parts.length === 3) {
    // หาตำแหน่งของปี (4 หลัก)
    const yearIndex = parts.findIndex((part) => part.length === 4);

    if (yearIndex === -1) {
      return null;
    }

    // แยกตามตำแหน่งของปี
    if (yearIndex === 0) {
      [year, month, day] = parts; // YYYY-MM-DD format
    } else if (yearIndex === 2) {
      [day, month, year] = parts; // DD/MM/YYYY format
    } else {
      return null; // ปีอยู่ตรงกลาง = รูปแบบไม่ถูกต้อง
    }
  } else if (parts.length === 2) {
    // 2 ส่วน: MM/YYYY หรือ YYYY-MM
    [month, year] = dateString.includes("/") ? parts : [parts[1], parts[0]];
  } else {
    // 1 ส่วน: เฉพาะปี
    year = parts[0];
  }

  if (year.length !== 4) {
    return null;
  }

  // แปลง ค.ศ. -> พ.ศ. ถ้าต้องการ BE format
  if (!useAdFormat) {
    const yearNum = Number.parseInt(year);

    // ถ้าปีน้อยกว่าเท่ากับปีปัจจุบัน แสดงว่าเป็น ค.ศ. ต้องแปลงเป็น พ.ศ.
    if (yearNum <= YEAR_THRESHOLD) {
      year = (yearNum + 543).toString();
    }
  }

  return useAdFormat
    ? `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
    : `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
};

/**
 * Convert AD or BE date string to Moment object (auto-detects format)
 * @param value - Date string in AD or BE format (supports incomplete dates)
 * @returns Moment object, or null if invalid or undefined input
 */
export const convertAdBeDateToMoment = (value?: string) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  if (isValidADDate(value)) {
    const formatted = formatMissingDate(value, "YYYY-MM-DD");

    return formatted ? moment(formatted) : null;
  }

  const formatted = formatMissingDate(value);

  return formatted ? parseDate(formatted, true) : null;
};

/**
 * Validate if string is a valid AD or BE date
 * @param date - Date string to validate (supports various formats: YYYY/MM/DD, YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY)
 * @returns True if date string is valid and can be converted to Moment, false otherwise
 */
export const isValidAdBeDate = (date?: string) => {
  // รูปแบบวันที่: YYYY/MM/DD, YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY
  const dateRegex = /\d{4}[/-]\d{1,2}[/-]\d{1,2}|\d{1,2}[/-]\d{1,2}[/-]\d{4}/;
  const isValidDateFormat = (date || "").search(dateRegex) >= 0;
  const momentDate = convertAdBeDateToMoment(date);

  return isValidDateFormat && !!momentDate;
};

/**
 * Get ordinal suffix for a number in English
 * @param number - Number to get ordinal for
 * @returns Object with number and suffix (st, nd, rd, or th)
 * @example getOrdinal(1) returns { number: 1, suffix: "st" }
 * @example getOrdinal(22) returns { number: 22, suffix: "nd" }
 */
export const getOrdinal = (number: number) => {
  const rules = new Intl.PluralRules("en", { type: "ordinal" });
  const suffixes: Record<string, string> = {
    few: "rd",
    other: "th",
    two: "nd",
    one: "st",
  };

  return { number, suffix: suffixes[rules.select(number)] };
};

/**
 * Convert Thai (BE) date string to ISO format (AD)
 * @param thaiDate - Date string in DD/MM/YYYY (BE) format
 * @returns ISO date string in YYYY-MM-DD (AD) format, or empty string if invalid/undefined
 * @example convertThaiDateToISO("15/01/2566") returns "2023-01-15"
 */

export const convertThaiDateToISO = (thaiDate?: string) => {
    if (!thaiDate) return "";
    const [day, month, year] = thaiDate.split("/").map((v) => parseInt(v, 10));
    const gregorianYear = year - 543;
    const dd = String(day).padStart(2, "0");
    const mm = String(month).padStart(2, "0");
    return `${gregorianYear}-${mm}-${dd}`;
  };


export const parseBeOrAdDate = (str: string) => {
  if (!str) return null;

  const [day, month, year] = str.split("/").map(Number);
  if (!day || !month || !year) return null;

  const adYear = year > 2500 ? year - 543 : year;
  return moment({ year: adYear, month: month - 1, day });
};

export const formatThaiDate = (dateString: any) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("th-TH", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

export const formatDateBE = (date: Date) => {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear() + 543;
  return `${dd}/${mm}/${yyyy}`;
};

export const toApiDate = (dateStr: string | undefined) => {
  if (!dateStr) return undefined;
  const ad = beStringToAdString(dateStr);
  if (!ad) return undefined;

  const [dd, mm, yyyy] = ad.split("/");
  return `${yyyy}-${mm}-${dd}`;
};


export const formatDateTimeToISO = (dateStr?: string, timeStr?: string) => {
  if (!dateStr) return undefined;
  const [day, month, year] = dateStr.split("/");
  const [hours = "00", minutes = "00"] = timeStr ? timeStr.split(":") : [];
  const jsDate = new Date(`${year}-${month}-${day}T${hours}:${minutes}:00`);
  return jsDate.toISOString();
};


/**
 * Convert date to DD/MM/YYYY format in Buddhist Era (BE)
 * @param date - Date object, Moment object, or date value to format
 * @returns Formatted date string DD/MM/YYYY (BE) or empty string if invalid
 */
export function formatDateAD(date: any) {
  if (moment.isMoment(date)) {
    date = date.toDate();
  }
  if (date instanceof Date && !isNaN(date.valueOf())) {
    const day = `${date.getDate()}`.padStart(2, "0");
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const year = `${date.getFullYear()}`;
    return `${year}-${month}-${day}`;
  }
  return "";
}

/**
 * Get Thai month name from date
 * @param date - Date object
 * @returns Thai month name (e.g., "มกราคม")
 */
export const formatThaiMonth = (date: Date): string => {
  const months = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
  ];
  return months[date.getMonth()];
};

/**
 * Get English month name from date
 * @param date - Date object
 * @returns English month name (e.g., "January")
 */
export const formatEnglishMonth = (date: Date): string => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[date.getMonth()];
};

/**
 * Convert AD year to Thai Buddhist year
 * @param date - Date object
 * @returns Buddhist year (AD year + 543)
 */
export const formatThaiYear = (date: Date): number => {
  return date.getFullYear() + 543;
};

/**
 * Extract time from Date object in HH:mm format with zero padding
 * @param date - Date object
 * @returns Time string in HH:mm format (e.g., "09:30", "14:05")
 */
export const formatTimeFromDate = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Format time range from start and end dates
 * @param start - Start date
 * @param end - End date
 * @returns Time range string in HH:mm - HH:mm format
 */
export const formatTimeRange = (start: Date, end: Date): string => {
  return `${formatTimeFromDate(start)} - ${formatTimeFromDate(end)}`;
};

/**
 * Check if two dates are on the same day
 * @param date1 - First date
 * @param date2 - Second date
 * @returns True if dates are on the same day, false otherwise
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return date1.getDate() === date2.getDate() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getFullYear() === date2.getFullYear();
};

/**
 * Check if a date is today
 * @param date - Date to check
 * @returns True if date is today, false otherwise
 */
export const isToday = (date: Date): boolean => {
  return isSameDay(date, new Date());
};

/**
 * Format Date object to DD/MM/YYYY (AD) format for input fields
 * @param date - Date object to format
 * @returns Date string in DD/MM/YYYY (AD) format
 */
export const formatDateForInput = (date: Date): string => {
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Convert time string from HH:MM:SS to HH:MM format
 * @param timeStr - Time string in HH:MM:SS or HH:MM format
 * @returns Time string in HH:MM format (default: "09:00" if empty)
 */
export const formatTimeFromString = (timeStr: string): string => {
  // Convert HH:MM:SS to HH:MM
  if (!timeStr) return '09:00';
  const parts = timeStr.split(':');
  return `${parts[0]}:${parts[1]}`;
};

/**
 * Convert BE Date DD/MM/YYYY (BE) to YYYY-MM-DD (AD) format
 * @param beDate - BE Date Ex. "01/12/2568"
 * @param inputFormat - Date Format (Default as "DD/MM/YYYY")
 */
export const convertBEDateToADDashDate = (beDate?: string, inputFormat = "DD/MM/YYYY"): string | undefined => {
  if (!beDate) return undefined;

  const m = moment(beDate, inputFormat, true);

  if (!m.isValid()) return undefined;

  const adYear = m.year() - 543;

  return m.year(adYear).format("YYYY-MM-DD");
};
// ================================================================================================
// BE wall-clock helpers
// backend (QUE ฯลฯ) ใช้ TIME_ZONE Asia/Bangkok และ USE_TZ=False → ค่า naive ที่ได้ออกมา
// เป็น wall clock ของ server อยู่แล้ว จึงอ่าน/เทียบค่าโดยไม่พึ่ง timezone ของ browser
// (กันเคส dev เครื่องเป็น UTC แล้ววัน/เวลาเลื่อนไป 7 ชั่วโมง)
// ================================================================================================
export const BE_UTC_OFFSET = "+07:00";

const HAS_TIMEZONE_RE = /Z|[+-]\d{2}:?\d{2}$/;

/**
 * parse ค่าวัน/เวลาจาก API ให้เป็น wall clock ของบางกอก
 * - naive (ไม่มี offset) = wall clock ของ server อยู่แล้ว คงไว้ตามนั้น
 * - aware (มี +07:00 / Z) = แปลงเป็นเวลาบางกอก
 */
export const beMoment = (value: any): Moment => {
  const raw = String(value ?? "");
  const parsed = moment(raw);

  return HAS_TIMEZONE_RE.test(raw) ? parsed.utcOffset(BE_UTC_OFFSET) : parsed;
};

export const beDateStr = (value: any): string => beMoment(value).format("YYYY-MM-DD");

export const beTimeStr = (value: any): string => beMoment(value).format("HH:mm");

/** "YYYY-MM-DD HH:mm" — ใช้เทียบวัน/เวลาว่าตรงกันจริงไหม (ข้าม timezone) */
export const beStampStr = (value: any): string => beMoment(value).format("YYYY-MM-DD HH:mm");

/** วันนี้ / ตอนนี้ ตามเวลาของ server */
export const beNowDateStr = (): string => moment().utcOffset(BE_UTC_OFFSET).format("YYYY-MM-DD");

export const beNowTimeStr = (): string => moment().utcOffset(BE_UTC_OFFSET).format("HH:mm");
